/**
 * Settings → Profile route.
 *
 * Profile lives inside the settings rail under the "Account" group (rail
 * position #1) so every self-serve preference sits in one place. Old
 * route `/profile` redirects here.
 *
 * Layout — a single `<Card>` containing five hairline-divided sections:
 *
 *   1. Cover            — avatar, name, role, contact + photo helper footer
 *   2. Account          — phone / email / recovery rows
 *   3. Company          — active workspace summary + quick links
 *   4. Theme            — compact preview-tile picker
 *   5. Activity         — vertical event timeline
 *
 * Sign-out sits outside the card as a destructive footer action. Each
 * section component renders just its content (no Card wrapper) — the
 * route owns the unified Card so the page reads as one cohesive panel.
 *
 * Data: everything reads from `identity.me` — no extra round-trip on
 * page load. Dialog mutations invalidate `me` on success so the topbar
 * avatar / dropdown header reflect changes immediately.
 */
import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { Alert, Button, Card, CardBody } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { meQueryOptions, meQueryKey } from "../../../../lib/session.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell } from "../../../../features/settings/index.ts";
import {
  ProfileCover,
  AccountCard,
  WorkspaceCard,
  ActivityCard,
  PreferencesCard,
  EditNameDialog,
  EditEmailDialog,
  VerifyEmailDialog,
} from "../../../../features/profile/index.ts";

export const Route = createFileRoute("/(workspace)/settings/profile/")({
  component: ProfilePage,
});

function ProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useQuery(meQueryOptions);

  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [verifyEmailOpen, setVerifyEmailOpen] = useState(false);

  const signOut = trpc.identity.signOut.useMutation({
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: meQueryKey });
      await navigate({ to: "/sign-in" });
    },
  });

  // Loading / error guards. The route guard at `__root.tsx` has already
  // ensured `me` resolved server-side, so the loading state here is the
  // brief in-app refetch window — usually <100ms.
  if (me.isLoading) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Profile" }]}>
        <SettingsShell activeSection="profile" pageTitle="Profile">
          <p className="text-muted">Loading profile…</p>
        </SettingsShell>
      </WorkspaceShell>
    );
  }
  if (me.error || !me.data) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Profile" }]}>
        <SettingsShell activeSection="profile" pageTitle="Profile">
          <Alert
            intent="bad"
            title="Couldn't load profile"
            description={me.error?.message ?? "Please try again."}
          />
        </SettingsShell>
      </WorkspaceShell>
    );
  }
  const { user, company } = me.data;

  const refreshMe = () => {
    void queryClient.invalidateQueries({ queryKey: meQueryKey });
  };

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Profile" }]}>
      <SettingsShell
        activeSection="profile"
        pageTitle="Profile"
        pageDescription="Your account — name, photo, email, phone, and the workspace you're signed in to."
      >
        {/* Single unified Card. `CardBody p-0 divide-y` lets each section
            render its own internal padding while the dividers automatically
            separate adjacent sections. */}
        <Card>
          <CardBody className="p-0 divide-y divide-line">
            <ProfileCover user={user} company={company} onEditName={() => setEditNameOpen(true)} />
            <AccountCard
              user={user}
              onEditEmail={() => setEditEmailOpen(true)}
              onVerifyEmail={() => setVerifyEmailOpen(true)}
            />
            {company ? <WorkspaceCard company={company} /> : null}
            <PreferencesCard />
            <ActivityCard user={user} />
          </CardBody>
        </Card>

        {/* Sign-out sits OUTSIDE the unified card as a destructive footer
            action — keeps it visually separated from the editable surfaces
            above so an errant click is less likely. */}
        <div className="flex justify-end pt-2">
          <Button
            intent="ghost"
            onClick={() => {
              if (signOut.isPending) return;
              signOut.mutate({});
            }}
            disabled={signOut.isPending}
            className="text-bad hover:bg-bad-50"
          >
            <LogOut size={14} strokeWidth={2} aria-hidden />
            {signOut.isPending ? "Signing out…" : "Sign out"}
          </Button>
        </div>
      </SettingsShell>

      <EditNameDialog
        open={editNameOpen}
        onOpenChange={setEditNameOpen}
        initialName={user.name}
        onUpdated={refreshMe}
      />
      <EditEmailDialog
        open={editEmailOpen}
        onOpenChange={setEditEmailOpen}
        initialEmail={user.email}
        onUpdated={refreshMe}
      />
      {user.email ? (
        <VerifyEmailDialog
          open={verifyEmailOpen}
          onOpenChange={setVerifyEmailOpen}
          email={user.email}
          onVerified={refreshMe}
        />
      ) : null}
    </WorkspaceShell>
  );
}
